/**
 * MCP CLI Pairing Consent Dialog
 *
 * Shows a dialog when the CLI requests pairing, displaying a verification code
 * that the user must confirm matches what they see in the terminal.
 */

import { Terminal, ShieldCheck, Clock } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import type { McpPairingApprovalRequest } from '../../../types/mcpPairing';

type McpPairingDialogProps = {
  request: McpPairingApprovalRequest | null;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
};

export function McpPairingDialog({ request, onApprove, onDeny }: McpPairingDialogProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!request) {
      setTimeRemaining(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const timeoutMs = request.timeoutMs ?? 120000;
    const startTime = Date.now();
    const endTime = startTime + timeoutMs;

    setTimeRemaining(Math.ceil((endTime - Date.now()) / 1000));

    timerRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setTimeRemaining(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [request]);

  if (!request) return null;

  const handleApprove = () => onApprove(request.requestId);
  const handleDeny = () => onDeny(request.requestId);

  return (
    <AlertDialog open={!!request} onOpenChange={(open) => !open && handleDeny()}>
      <AlertDialogContent className="max-w-md" data-testid="mcp-pairing-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <Terminal className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg">
                CLI Pairing Request
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                <span className="font-medium">{request.clientName}</span> wants to pair with Jam SQL Studio
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
            <span>Verify this code matches what you see in your terminal:</span>
          </div>

          <div className="flex items-center justify-center rounded-lg border-2 border-blue-500/30 bg-blue-500/5 p-6">
            <span
              className="font-mono text-3xl font-bold tracking-widest text-foreground"
              data-testid="mcp-pairing-code"
            >
              {request.userCode}
            </span>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Approving grants the CLI access to your MCP server token.
            Only approve if you initiated this pairing.
          </p>

          {timeRemaining !== null && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {timeRemaining > 0
                  ? `Expires in ${timeRemaining}s`
                  : 'Request expired'}
              </span>
            </div>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDeny}
            data-testid="mcp-pairing-deny"
          >
            Deny
          </Button>
          <Button
            variant="default"
            onClick={handleApprove}
            data-testid="mcp-pairing-approve"
            disabled={timeRemaining === 0}
          >
            Approve Pairing
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
